import React, { useEffect, useState } from 'react';
import api from '../services/api';

// Renders an authenticated image. The /api/uploads/:filename endpoint
// requires the JWT, which a plain `<img src>` tag can't supply via
// header — so we fetch as a blob, hand React an object URL, and revoke
// it on unmount.
//
// Pass either `filename` (just the basename) or `src` (a path like
// `/uploads/abc.jpg` from legacy storage, which we strip down to the
// basename and re-route through /api/uploads).
//
// All other props are forwarded to the underlying <img>.
export default function AuthImg({ filename, src, alt = '', fallback = null, ...imgProps }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    // Accept legacy `src="/uploads/abc.jpg"` too — strip to bare path
    // AFTER the /uploads/ prefix so files in sub-folders (e.g. signatures/)
    // reach the right nested route. v1.9.37 — was blanket-encoding the whole
    // remainder including the '/', which turned 'signatures/sig-x.png' into
    // 'signatures%2Fsig-x.png' and 404'd every signature preview.
    const stripped = filename || (src ? src.replace(/^.*\/api\/uploads\//, '').replace(/^.*\/uploads\//, '') : '');
    if (!stripped) { setError(true); return; }
    // Preserve any sub-folder segments literally; only encode each path part.
    const encodedPath = stripped.split('/').map(encodeURIComponent).join('/');

    api.get(`/uploads/${encodedPath}`, { responseType: 'blob' })
      .then(r => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(r.data);
        setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setError(true); });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [filename, src]);

  if (error) return fallback;
  if (!url) return fallback;
  return <img src={url} alt={alt} {...imgProps} />;
}
